import { useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { RuleCategory } from "../../generated/graphql";
import PSelect, { TypedOptionalSelect } from "../../components/PSelect";
import FormFieldAutoComplete from "../../components/FormFieldAutocomplete";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  editAllSelectedRules,
  selectAllFieldRules,
  selectAllSubtypes,
  selectLensEditorForm,
  selectLensEditorLens,
  selectSelectedRules,
} from "../../redux/slices/lensEditor";
import { distinct } from "helpers/coll";
import { isValidPrintableASCII } from "Backend";

export interface BulkEditDialogProps {
  open: boolean;
  close: () => void;
}

const BulkEditDialog = (props: BulkEditDialogProps) => {
  const { open, close } = props;

  const lens = useAppSelector(selectLensEditorLens);

  const [newCategory, setNewCategory] = useState<RuleCategory>();
  const [newSubtype, setNewSubtype] = useState<string>();
  const [newDescription, setNewDescription] = useState<string>();
  const [newWeight, setNewWeight] = useState<number>();
  const [newFieldId, setNewFieldId] = useState<number>();

  const form = useAppSelector(selectLensEditorForm);

  const dispatch = useAppDispatch();
  const allSubtypes = useAppSelector(selectAllSubtypes);
  const allFieldRules = useAppSelector(selectAllFieldRules);
  const decriptionOptions = useMemo(
    () => distinct(allFieldRules.filter((rule) => rule.subtype === newSubtype).map((r) => r.description)),
    [newSubtype, allFieldRules]
  );
  const selectedRules = useAppSelector(selectSelectedRules);
  const ruleCount = selectedRules.length;

  const isValidSubtype = newSubtype !== undefined ? isValidPrintableASCII(newSubtype) : true;
  const isValidDescription = newDescription !== undefined ? isValidPrintableASCII(newDescription) : true;
  const canUpdate = isValidSubtype && isValidDescription;

  const runUpdate = async () => {
    await dispatch(
      editAllSelectedRules({ lensId: lens.id, newCategory, newSubtype, newDescription, newFieldId, newWeight })
    );
    close();
  };

  return (
    <Dialog open={open} onClose={close}>
      <DialogTitle>Bulk Edit Lens Rules</DialogTitle>
      <DialogContent sx={{ display: "flex", p: 2, m: 2, gap: 2, flexDirection: "column", width: "400px" }}>
        <TypedOptionalSelect<RuleCategory>
          label="New Category"
          options={[RuleCategory.Product, RuleCategory.Injury, RuleCategory.Other]}
          value={newCategory}
          onChange={(cat) => setNewCategory(cat)}
        />
        <Autocomplete
          freeSolo
          options={allSubtypes}
          inputValue={newSubtype}
          onInputChange={(_evt, newInputValue) => {
            setNewSubtype(newInputValue);
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                error={!isValidSubtype}
                helperText={!isValidSubtype ? "Invalid input. Only printable ASCII characters are allowed." : ""}
                label="New Subtype"
                margin="none"
              />
            );
          }}
        />
        <Autocomplete
          freeSolo
          options={decriptionOptions}
          inputValue={newDescription}
          onInputChange={(_evt, newInputValue) => {
            setNewDescription(newInputValue);
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                error={!isValidDescription}
                helperText={!isValidDescription ? "Invalid input. Only printable ASCII characters are allowed." : ""}
                label="New Description"
                margin="none"
              />
            );
          }}
        />
        <FormFieldAutoComplete
          selected={form.fields.find((field) => field.id === newFieldId)}
          options={form.fields}
          onChange={(field) => setNewFieldId(field?.id)}
        />
        <FormControl>
          <FormLabel>Weight</FormLabel>
          <PSelect
            type="number"
            value={newWeight || ""}
            onChange={(evt) => {
              setNewWeight(evt.target.value as number);
            }}>
            <MenuItem value={0}>0</MenuItem>
            <MenuItem value={1}>1</MenuItem>
          </PSelect>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" onClick={runUpdate} disabled={!canUpdate}>
          Update {ruleCount} Rules
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditDialog;
